<script setup lang="ts">
import { computed } from "vue";
import { assertNever } from "../../lib/assertNever";
import { isJust, just, nothing, fromMaybe } from "../../lib/maybe";
import { useTea } from "../../lib/tea";
import { groupSceneLinkEntries } from "../../lib/sceneLinkOptions";
import { listUnmappedArcMoments } from "../../lib/storyboardArcs";
import { displayFileName } from "../../lib/fileNames";
import { entityEffectiveColor } from "../../lib/entityColors";
import { useOutlineEntitiesStore } from "../../stores/useOutlineEntitiesStore";
import { useStoryboardDragStore } from "../../stores/useStoryboardDragStore";
import type { StoryboardEntityChip } from "../storyboard/SceneMetadata.vue";
import ZwMenu from "../ui/ZwMenu.vue";
import ZwMenuItem from "../ui/ZwMenuItem.vue";
import {
  initialModel,
  update,
  type Cmd,
  type LinkTarget,
} from "./SceneLinkPicker.update";

const props = defineProps<{
  scenePath: string;
  chips: StoryboardEntityChip[];
}>();
const entities = useOutlineEntitiesStore();
const links = useStoryboardDragStore();
const { model, dispatch } = useTea(initialModel, update, {
  runCmd: just(runCmd),
});
const open = computed({
  get: () => model.value.open,
  set: (open: boolean) => {
    void dispatch({ type: "OpenChanged", open });
  },
});
const groups = computed(() => {
  const linked = new Set(
    props.chips.flatMap((chip) =>
      isJust(chip.entryFile) ? [chip.entryFile.value] : [],
    ),
  );
  return groupSceneLinkEntries(
    entities.databaseEntryFields.filter(
      (entry) => linked.has(entry.name) === false,
    ),
  );
});
const moments = computed(() =>
  listUnmappedArcMoments(entities.databaseEntryFields),
);
function select(target: LinkTarget): void {
  void dispatch({ type: "Select", target, scenePath: props.scenePath });
}
async function runCmd(cmd: Cmd): Promise<void> {
  try {
    switch (cmd.target.kind) {
      case "moment":
        await links.linkArcMoment(
          cmd.target.entryFile,
          cmd.target.momentId,
          cmd.scenePath,
        );
        break;
      case "entity":
        await links.setSceneLink(cmd.target.entryFile, cmd.scenePath, true);
        break;
      default:
        assertNever(cmd.target);
    }
    await dispatch({ type: "Saved" });
  } catch (error) {
    await dispatch({
      type: "Failed",
      error:
        error instanceof Error
          ? error.message
          : "Unable to add this link. Try again.",
    });
  }
}
</script>
<template>
  <ZwMenu
    v-model:open="open"
    align="end"
    :menu-label="just('Add scene link')"
    :disabled="model.busy"
  >
    <template #trigger="{ toggle, attrs }">
      <button
        type="button"
        class="scene-link-add"
        aria-label="Add scene link"
        :disabled="model.busy"
        v-bind="attrs"
        @click="toggle()"
      >
        {{ model.busy ? "Adding…" : "+ Add" }}
      </button>
    </template>
    <div class="scene-link-options">
      <section v-if="moments.length > 0" aria-label="Story arc">
        <h3>Story arc</h3>
        <ZwMenuItem
          v-for="moment in moments"
          :key="moment.id"
          :label="`${moment.entityLabel} · ${moment.moment}`"
          :shortcut="nothing"
          :title="moment.at"
          @select="
            select({
              kind: 'moment',
              entryFile: moment.entryFile,
              momentId: moment.momentId,
            })
          "
        />
      </section>
      <section v-for="[type, entries] in groups" :key="type" :aria-label="type">
        <h3>{{ type }}</h3>
        <div
          v-for="entry in entries"
          :key="entry.name"
          class="scene-link-option"
        >
          <span
            class="scene-link-dot"
            aria-hidden="true"
            :style="{
              background:
                fromMaybe(
                  entityEffectiveColor(entry.fields.color, entry.fields.type),
                ) ?? 'var(--text-muted)',
            }"
          />
          <ZwMenuItem
            :label="displayFileName(entry.name)"
            :shortcut="nothing"
            :title="nothing"
            @select="select({ kind: 'entity', entryFile: entry.name })"
          />
        </div>
      </section>
      <p
        v-if="groups.length === 0 && moments.length === 0"
        class="scene-link-empty"
      >
        No more entries or unlinked events to add.
      </p>
    </div>
  </ZwMenu>
  <p v-if="isJust(model.error)" class="scene-link-error" role="alert">
    {{ model.error.value }}
  </p>
</template>
<style scoped>
.scene-link-add {
  border: 0;
  border-radius: 1rem;
  background: transparent;
  padding: 2px var(--space-sm);
  color: var(--text-muted);
  font: inherit;
  font-size: 0.75rem;
  cursor: pointer;
}
.scene-link-add:hover {
  background: var(--hover-fill);
  color: var(--text);
}
.scene-link-add:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 2px;
}
.scene-link-add:disabled {
  cursor: wait;
}
.scene-link-options {
  width: min(18rem, 75vw);
}
.scene-link-options section + section {
  margin-top: var(--space-md);
}
h3 {
  margin: var(--space-sm);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
}
.scene-link-option {
  position: relative;
}
.scene-link-option :deep(.zw-menu-item) {
  padding-left: 1.3rem;
}
.scene-link-dot {
  position: absolute;
  left: 0.45rem;
  top: 0.85rem;
  width: 0.4rem;
  height: 0.4rem;
  border-radius: 50%;
  pointer-events: none;
}
.scene-link-options :deep(.zw-menu-item-label) {
  white-space: normal;
  overflow-wrap: anywhere;
}
.scene-link-empty,
.scene-link-error {
  padding: var(--space-sm);
  font-size: 0.8rem;
  color: var(--text-muted);
}
@media (pointer: coarse) {
  .scene-link-add {
    min-height: 44px;
  }
}
</style>
